import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DamageService } from '../../services/damage.service';
import { NotificationService } from '../../services/notification.service';
import { catchError, take, tap } from 'rxjs/operators';
import { EMPTY, from } from 'rxjs';
import { Signature } from '../../invoice/models/signature.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SignatureInputComponent } from '../../signature-input/signature-input.component';
import { LoadingService } from '../../services/loading.service';
import { Fault } from '../models/fault.model';
import { EventsService } from '../../services/events.service';
import { PathFinderService } from '../../services/path-finder.service';

@Component({
  selector: 'app-appraisal-report',
  templateUrl: './appraisal-report.component.html',
  styleUrls: ['./appraisal-report.component.scss']
})
export class AppraisalReportComponent implements OnInit, OnChanges {

  @Input() quoteStateId: number;
  @Input() group: UntypedFormGroup;
  @Input() canLoadData: boolean;

  loading: boolean;
  isDataReady: boolean;
  isInitialised: boolean;
  reportContent: string;
  damageItems: Array<Fault>;
  customerSignature: Signature;
  customerContent: string;
  customerDamageItems: Array<Fault>;
  source: string;
  hasDamages: boolean;
  hasGradeChanged: boolean;

  constructor(private damageService: DamageService,
    private notifications: NotificationService, private modalService: NgbModal, private loadingService: LoadingService,
    private eventService: EventsService, private pathFinder: PathFinderService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.canLoadData.firstChange && changes.canLoadData.currentValue === undefined) {
      this.loadData(true);
    }
  }

  ngOnInit(): void {
    this.loading = true;
    this.loadingService.loading.emit(this.loading);
    setTimeout(() => {
      this.group.controls.value.setValue(null);
    });

    this.eventService.answersSaved.pipe(take(1)).subscribe(value => {
      this.loadData(value);
    });
    this.eventService.authAswersChanged.pipe(take(1)).subscribe(() => {
      this.loading = true;
      this.loadingService.loading.emit(this.loading);
      this.group.controls.value.setValue(null);
      this.customerSignature = null;
      if (this.pathFinder.getAnswerValue('Mechanic Override Grade Assessment', null)) {
        this.hasGradeChanged = true;
      }
      this.damageService.postDamage$(this.quoteStateId, []).pipe(
        tap(result => {
          this.pathFinder.setValuationAmount(result);
          this.retrieveAppraisalData();
        }),
        catchError(err => {
          this.loading = false;
          this.loadingService.loading.emit(this.loading);
          this.notifications.dangerToast('Failed to retrieve appraisal report', err);
          return EMPTY;
        }),
        take(1)
      ).subscribe();
    });
  }

  private loadData(value: boolean) {
    this.loading = true;
    this.loadingService.loading.emit(this.loading);
    this.isDataReady = value;
    this.retrieveAppraisalData();
  }

  retrieveAppraisalData() {
    if (this.isDataReady) {
      this.damageService.getAppraisalReportContent$(this.quoteStateId).pipe(
        tap(result => {
          this.reportContent = result.reportHtml;
          this.damageItems = result.damageItems;
          this.customerContent = result.customerReportHtml;
          this.customerDamageItems = result.customerDamageItems;
          this.hasDamages = result.customerDamageItems.length > 0;
          this.source = result.source;
          this.loading = false;
          this.loadingService.loading.emit(this.loading);
        }),
        catchError(err => {
          this.loading = false;
          this.loadingService.loading.emit(this.loading);
          this.notifications.dangerToast('Failed to retrieve appraisal report', err);
          return EMPTY;
        }),
        take(1)
      ).subscribe();
    }
  }

  sign() {
    const modalRef = this.modalService.open(SignatureInputComponent, {
      keyboard: false,
      backdrop: 'static',
      centered: true,
      size: 'xl',
      windowClass: 'signatureWindow'
    });
    modalRef.componentInstance.isWbacSignature = false;
    modalRef.componentInstance.quoteStateId = this.quoteStateId;
    modalRef.componentInstance.disclaimerPrefix = 'Vendor:';
    modalRef.componentInstance.disclaimer = 'I confirm this is an accurate description of my vehicle';
    modalRef.result.then(result => {
      if (result === undefined) {
        this.loadingService.loading.emit(false);
        this.modalService.dismissAll();
        return from(modalRef.result).pipe(catchError(() => EMPTY))
      }
      this.customerSignature = result;
      this.group.controls.value.setValue(this.damageItems.length > 0);
      this.loadingService.loading.emit(true);
      this.damageService.generateAppraisalReport$(this.quoteStateId, this.customerSignature).pipe(
        tap(() => {
          this.loadingService.loading.emit(false);
        }),
        catchError(err => {
          this.notifications.dangerToast(`Failed to generate appraisal report for purchaseId: ${this.quoteStateId}`, err);
          this.loadingService.loading.emit(false);
          return EMPTY;
        }),
        take(1)
      ).subscribe();
    }, () => { });
  }
}
